import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "./button";
import {
  UserRound,
  Crown,
  Gamepad,
  Loader2,
  MessageCircle,
  Users,
  User,
  MoreVertical,
  UserMinus,
  UserX,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { UserProfileLink } from "./user-profile-link";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useStableSocket } from "@/hooks/use-stable-socket";
import { useEffect, useState, useCallback } from "react";
import WebSocketService from "@/lib/websocket-service";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface FriendListProps {
  readonly searchQuery?: string;
  readonly filter?: string;
}

// Extended friend type to include more accurate typing
interface FriendWithProfile {
  id: number;
  userId: number;
  friendId: number;
  status: string;
  createdAt: string;
  username: string;
  isOnline?: boolean;
  lastActive?: string;
  profile?: {
    avatarId?: number | null;
    avatarSource?: string;
    level?: number;
    status?: string;
  };
}

export function FriendList({
  searchQuery = "",
  filter = "all",
}: Readonly<FriendListProps>) {
  const { user, token } = useAuth();
  const currentUserId = user?.id;
  const [onlineFriends, setOnlineFriends] = useState<Set<number>>(new Set());
  const { toast } = useToast();
  const [, navigate] = useLocation();
  
  const {
    isConnected,
    authStatus,
    sendMessage,
    registerMessageCallback,
  } = useStableSocket({
    autoAuth: true,
    silentReconnect: true,
    debug: true
  });

  const {
    data: friends,
    refetch,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message ?? "Failed to fetch friends");
      }
      return res.json();
    },
  });

  // Store avatar data for all friends
  const friendAvatars = useQuery({
    queryKey: ["friend-avatars", friends],
    queryFn: async () => {
      if (!friends?.length) return {};
      
      const avatarData: Record<number, { imageUrl: string | null }> = {};
      
      for (const friend of friends) {
        if (!friend.profile?.avatarId) continue;
        
        
        const source = friend.profile.avatarSource || "default";
        const endpoint = source === "shop" 
          ? `/api/customization/shopItem/${friend.profile.avatarId}` 
          : `/api/customization/avatars/${friend.profile.avatarId}`;
        
          
        try {
          const res = await apiRequest("GET", endpoint);
          if (res.ok) {
            const data = await res.json();
            avatarData[friend.userId === currentUserId ? friend.friendId : friend.userId] = data;
          }
        } catch (error) {
          console.error("Error fetching avatar:", error);
        }
      }
      
      return avatarData;
    },
    enabled: !!friends?.length,
  });

  



  // Handle friend online status updates
  useEffect(() => {
    const handleOnlineStatus = (e: CustomEvent) => {
      const { onlineUsers } = e.detail;
      setOnlineFriends(new Set(onlineUsers));
    };

    const handleStatusChange = (e: CustomEvent) => {
      const { userId, isOnline } = e.detail;
      setOnlineFriends(prev => {
        const newSet = new Set(prev);
        if (isOnline) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    };

    // Add event listeners
    window.addEventListener('friend_online_status', handleOnlineStatus as EventListener);
    window.addEventListener('friend_status_change', handleStatusChange as EventListener);

    // Clean up
    return () => {
      window.removeEventListener('friend_online_status', handleOnlineStatus as EventListener);
      window.removeEventListener('friend_status_change', handleStatusChange as EventListener);
    };
  }, []);

  // Function to request online status of friends with retry logic
  const requestFriendsOnlineStatus = useCallback(() => {
    const wsService = WebSocketService.getInstance();
    if (friends?.length > 0) {
      const friendUserIds = friends
        .filter((friend: any) => friend.status === "accepted")
        .map((friend: any) => friend.userId === currentUserId ? friend.friendId : friend.userId);
      
      if (friendUserIds.length > 0) {
        wsService.requestOnlineStatus(friendUserIds);
      }
    }
  }, [friends, currentUserId]);

  // Subscribe to status updates when friends list changes
  useEffect(() => {
    if (isConnected && authStatus === 'authenticated') {
      requestFriendsOnlineStatus();
    }
  }, [isConnected, authStatus, requestFriendsOnlineStatus]);

  // Handle WebSocket authentication success
  useEffect(() => {
    const handleAuthSuccess = (data: any) => {
      // console.log("WebSocket authenticated successfully", data);
      // Add small delay before requesting friend status to ensure server auth is processed
      setTimeout(requestFriendsOnlineStatus, 100);
    };
    
    const removeAuthSuccessHandler = registerMessageCallback("MESSAGING_AUTH_SUCCESS", handleAuthSuccess);
    return () => removeAuthSuccessHandler();
  }, [registerMessageCallback, requestFriendsOnlineStatus]);

  // Also handle ERROR messages to retry authentication if needed
  useEffect(() => {
    const handleError = (data: any) => {
      console.log("[FriendList] Received error:", data);
      if (data.code === 'AUTH_REQUIRED' && isConnected && token) {
        // console.log("[FriendList] Retrying authentication");
        sendMessage({
          type: "MESSAGING_AUTH",
          token
        });
      }
    };
    
    const removeErrorHandler = registerMessageCallback("ERROR", handleError);
    return () => removeErrorHandler();
  }, [registerMessageCallback, isConnected, token, sendMessage]);

  // Handle manual authentication when connected
  useEffect(() => {
    if (isConnected && token && authStatus === 'none') {
      // console.log("WebSocket connected, sending authentication");
      sendMessage({
        type: "MESSAGING_AUTH",
        token
      });
    }
  }, [isConnected, token, authStatus, sendMessage]);

  // Monitor connection status
  useEffect(() => {
    console.log("WebSocket connection status:", isConnected);
  }, [isConnected]);

  // Listen for online status updates
  useEffect(() => {
    const removeOnlineStatusHandler = registerMessageCallback("ONLINE_STATUS", (data) => {
      console.log("[FriendList] Received ONLINE_STATUS:", data);
      if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
        setOnlineFriends(new Set(data.onlineUsers));
      }
    });
    
    const removeUserOnlineHandler = registerMessageCallback("USER_ONLINE", (data) => {
      console.log("[FriendList] User came online:", data);
      if (data.userId) {
        setOnlineFriends(prev => {
          const newSet = new Set(prev);
          newSet.add(data.userId);
          return newSet;
        });
      }
    });
    
    const removeUserOfflineHandler = registerMessageCallback("USER_OFFLINE", (data) => {
      console.log("[FriendList] User went offline:", data);
      if (data.userId) {
        setOnlineFriends(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      }
    });
    
    return () => {
      removeOnlineStatusHandler();
      removeUserOnlineHandler();
      removeUserOfflineHandler();
    };
  }, [registerMessageCallback]);

  // Request online status when authentication and friends data are ready
  useEffect(() => {
    requestFriendsOnlineStatus();
  }, [requestFriendsOnlineStatus]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  // Handle game invites
  const handleGameInvite = async (friendshipId: number) => {
    try {
      const response = await apiRequest("POST", "/api/game-rooms/private", {
        friendshipId: friendshipId,
      });

      if (response.ok) {
        const game = await response.json();
        toast({
          title: "Game created",
          description: "Your friend will be notified to join",
        });
        navigate(`/game/${game.id}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message ?? "Failed to create game");
      }
    } catch (error) {
      console.error("Error creating game:", error);
      toast({
        title: "Failed to create game",
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive",
      });
    }
  };

  // Handle friend removal
  const removeFriend = useMutation({
    mutationFn: async (friendId: number) => {
      const res = await apiRequest("DELETE", "/api/friends/remove", {
        friendId
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message ?? "Failed to remove friend");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Friend removed",
        description: "The friend has been removed from your list",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove friend",
        description: error.message ?? "Please try again later",
        variant: "destructive",
      });
    },
  });

  // Handle friend blocking
  const blockFriend = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", "/api/friends/block", {
        userId
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message ?? "Failed to block user");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "User blocked",
        description: "This user has been blocked",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to block user",
        description: error.message ?? "Please try again later",
        variant: "destructive",
      });
    },
  });

  // Handle friend unblocking
  const unblockFriend = useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiRequest("POST", "/api/friends/unblock", {
        userId
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message ?? "Failed to unblock user");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "User unblocked",
        description: "This user has been unblocked",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to unblock user",
        description: error.message ?? "Please try again later",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        <p>Loading your friends...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <Users className="w-10 h-10 opacity-30 mb-4" />
        <p className="text-lg mb-1">Could not load friends</p>
        <p className="text-sm text-white/40">Please try again later</p>
        <Button 
          variant="outline" 
          className="mt-4 bg-white/5 hover:bg-white/10 border-white/10"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Filter friends based on search query and filter type
  const filteredFriends = friends?.filter((friend: FriendWithProfile) => {
    const matchesSearch = friend.username.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === "blocked") {
      return matchesSearch && friend.status === "blocked" && friend.userId === currentUserId;
    }
    
    const matchesStatus = friend.status === "accepted";
    
    // Apply additional filtering based on online status
    if (filter === "online") {
      const friendId = friend.userId === currentUserId ? friend.friendId : friend.userId;
      return matchesSearch && matchesStatus && onlineFriends.has(friendId);
    } else if (filter === "offline") {
      const friendId = friend.userId === currentUserId ? friend.friendId : friend.userId;
      return matchesSearch && matchesStatus && !onlineFriends.has(friendId);
    }
    
    return matchesSearch && matchesStatus;
  });

  // Message to display when no friends are found
  const getEmptyMessage = () => {
    if (searchQuery) {
      return "Try a different search term";
    }
    
    if (filter === "blocked") {
      return "You haven't blocked anyone";
    }
    
    if (filter !== "all") {
      return `No friends are currently ${filter}`;
    }
    
    return "Add some friends to get started";
  };

  if (!filteredFriends?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <UserRound className="w-10 h-10 opacity-30 mb-4" />
        <p className="text-lg mb-1">No friends found</p>
        <p className="text-sm text-white/40">
          {getEmptyMessage()}
        </p>
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-3"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {filteredFriends.map((friend: FriendWithProfile) => {
        // Determine the friend's user ID (either userId or friendId, depending on which one is NOT the current user)
        const friendUserId = friend.userId === currentUserId ? friend.friendId : friend.userId;
        // Use the real online status from websocket
        const isOnline = onlineFriends.has(friendUserId);
        const avatarData = friendAvatars.data?.[friendUserId];
        
        // Show a different UI for blocked users
        if (friend.status === "blocked" && friend.userId === currentUserId) {
          return (
            <motion.div 
              key={friend.id} 
              className={cn(
                "flex justify-between items-center p-3 rounded-lg backdrop-blur-sm",
                "border border-red-900/20 hover:border-red-900/30",
                "bg-gradient-to-r from-black/60 to-black/40"
              )}
              variants={itemVariants}
            >
              <div className="flex items-center gap-3">
                <div className="relative opacity-70">
                  <Avatar className="h-10 w-10 ring-1 ring-red-900/50">
                    {avatarData?.imageUrl ? (
                      <AvatarImage src={avatarData.imageUrl} className="object-cover grayscale" />
                    ) : (
                      <AvatarFallback className="bg-black/70 text-red-500/70 font-medium">
                        {friend.username.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>
                
                <div className="flex flex-col">
                  <h3 className="text-sm font-medium text-white/70">
                    {friend.username}
                  </h3>
                  <div className="text-xs text-red-400/60">
                    Blocked
                  </div>
                </div>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-3 bg-transparent border-red-900/30 hover:bg-red-900/10 text-red-400/80 rounded-full"
                  >
                    <span className="text-xs">Unblock</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unblock User</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to unblock this user? They will be able to send you friend requests again.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => unblockFriend.mutate(friendUserId)}
                    >
                      Unblock
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </motion.div>
          );
        }
        
        // Regular friend UI for non-blocked friends
        return (
          <motion.div 
            key={friend.id} 
            className={cn(
              "flex justify-between items-center p-3 rounded-lg backdrop-blur-sm",
              "border border-white/5 hover:border-primary/20",
              "bg-gradient-to-r from-black/60 to-black/40"
            )}
            variants={itemVariants}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <UserProfileLink userId={friendUserId} username={friend.username}>
                  <Avatar className="h-10 w-10 ring-1 ring-black/70 cursor-pointer hover:ring-primary/30 transition-all duration-200">
                    {avatarData?.imageUrl ? (
                      <AvatarImage src={avatarData.imageUrl} className="object-cover" />
                    ) : (
                      <AvatarFallback className="bg-black text-primary font-medium">
                        {friend.username.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </UserProfileLink>
                
                {/* Online status indicator */}
                <div className={cn(
                  "absolute -bottom-1 -right-1 w-3 h-3 rounded-full border border-black",
                  isOnline ? "bg-green-500" : "bg-gray-500"
                )}/>
              </div>
              
              <div className="flex flex-col">
                <UserProfileLink userId={friendUserId} username={friend.username}>
                  <h3 className="text-sm font-medium text-white cursor-pointer hover:text-primary transition-colors">
                    {friend.username}
                    {friend.profile?.status === "premium" && (
                      <Crown className="h-3 w-3 text-yellow-400 inline-block ml-1 mb-0.5" />
                    )}
                  </h3>
                </UserProfileLink>
                
                <div className="text-xs text-white/50">
                  {isOnline ? "Online" : "Offline"}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              

              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/40 hover:bg-black/20 text-white w-7 h-7"
                onClick={() => navigate(`/messages/conversation/${friendUserId}`)}
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </Button>

              <Button
                variant="default"
                size="sm"
                className="h-7 px-3 bg-green-600/90 hover:bg-green-500 text-white border-0 rounded-full"
                onClick={() => handleGameInvite(friend.id)}
              >
                <Gamepad className="h-3 w-3 mr-1" />
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full bg-black/40 hover:bg-black/20 text-white w-7 h-7"
                  >
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <div className="flex flex-col space-y-1">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start w-full"
                        >
                          <UserMinus className="h-4 w-4 mr-2" />
                          Remove Friend
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Friend</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove this friend?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeFriend.mutate(friendUserId)}
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="justify-start w-full"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Block Friend
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Block Friend</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to block this friend?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => blockFriend.mutate(friendUserId)}
                          >
                            Block
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}